import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import { RootState, AppDispatch } from "configureStore";
import HeaderBadge from "components/header-badge/header-badge";
import { toggleExpanded } from "./presence.slice";

/**
 * Top-bar button for screens under 1024px, where the online-users rail is not drawn:
 * it opens the same list and chats as a sheet over the page (see `OnlineUsersPanel`).
 * The number on the people is who is online; the red badge is unread chat messages.
 */
export const OnlineRailToggle: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { self, online, expanded } = useSelector((state: RootState) => state.presenceReducer);
  const conversations = useSelector((state: RootState) => state.chatReducer.conversations);

  const totalOnline = online.length + (self ? 1 : 0);
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  const onlineLabel = t("online_count", { count: totalOnline });
  const label = totalUnread > 0 ? `${onlineLabel}, ${t("chats")}: ${totalUnread}` : onlineLabel;

  return (
    <button
      type="button"
      onClick={() => dispatch(toggleExpanded())}
      aria-expanded={expanded}
      aria-label={label}
      title={label}
      className="relative flex min-h-11 min-w-11 items-center justify-center rounded-sm border-none bg-transparent transition-colors hover:bg-gray-100 focus:outline-none lg:hidden"
    >
      <Users className="h-5 w-5 text-gray-600" />
      {totalOnline > 0 && (
        <span className="absolute bottom-1 right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-green-600 px-1 text-[10px] font-bold leading-none tabular-nums text-white ring-2 ring-white">
          {totalOnline > 99 ? "99+" : totalOnline}
        </span>
      )}
      <HeaderBadge count={totalUnread} />
    </button>
  );
};
